import React, { Component } from 'react';
import { findDOMNode } from 'react-dom';
import PropTypes from 'prop-types';
import axios from 'axios/index';
import animations from './animations';
import globals from '../../globals';
import Heading from '../../elements/Heading';
import SpeedButtons from '../../modules/SpeedButtons';
import Errors from '../../elements/Errors';
import PricingError from '../../modules/PricingError';
import Disclaimer1 from "../../elements/Disclaimer1";
import InternetForm from "../InternetForm";
import PricingBtn from "../../elements/PricingBtn";
import InternalPricingError from "../../elements/InternalPricingError";
import LoaderText from "../../elements/LoaderText";
import { CARRIERS, mapCarriersToData } from "../../constants";
import PlanResultsTableAsync from "../../modules/PlanResultsTableAsync";
import { sha256 } from 'js-sha256';

class PriceCheckerForm extends Component {

    constructor(props) {
        super(props);

        this.honeypot = 'dob';
        this.state = {
        	address: '', // is set by Addressify
            first_name: globals.getSessionVar('first_name'),
            last_name: globals.getSessionVar('last_name'),
            company: globals.getSessionVar('company'),
            email: globals.getSessionVar('email'),
            phone: globals.getSessionVar('phone'),
			speed: props.speeds[2],
            [this.honeypot]: '', //honeypot
            errors: [],
			formSubmitted: false,

            carriersLoaded: [],
            carriersDisabled: [],
            carrierErrors: [],
            plans: {},
            countCarriersLoaded: 0,
            countCarriersWithData: 0,
            
			inProgress: false
        };
    }

	componentDidMount() {
		this.controller = globals.genericAnimation(this.content);
	}

	componentWillUnmount() {
		globals.destroy(this.controller);
	}

    handleInputChange(event) {
        const target = event.target;
        this.setState({
            [target.name]: target.value
        });
    }

    handleSubmit(event) {
        event.preventDefault();
        this.reset();
        animations.showLoader(this);
        this.setProgress(true);
        setTimeout(() => {
			this.validateCustomerDetails();
        }, 500);
    }

    handleSpeedChange(value, event) {
    	if (!this.state.inProgress) {
			if (event) {
				animations.setActive(event.currentTarget);
			}

			this.setState({
				speed: value,
				plans: {}
			}, () => {
				if (this.state.formSubmitted) {
					this.getPricing();
				}
			});

            globals.pushEvent(this.props.eventNameSpeed, {speed: value});
		}
    }

	handleSetAddress(address) {
    	this.setState({address: address});
	}

	validateCustomerDetails() {
        axios.post('/api/validate-price-checker-form', this.state)
            .then(() => {
                this.setState({formSubmitted: true});
                this.submitCustomerDetails().then(() => {

                });
                this.getPricing();
            })
            .catch(response => {
                this.reset();
                this.setProgress(false);
                if (response.response.status === 422) {
                    this.processValidation(response.response);
                } else {
                    this.handleError(response);
                }
            });
    }

	async submitCustomerDetails() {
		await axios.post(this.props.submitFormUrl, this.state)
			.then(() => {
                globals.pushGA4Events(this.props.eventNameSubmit,
                    {
                        eventInfo: {
                            planName: this.state.speed,
                            hashedFirstName: sha256(this.state.first_name),
                            hashedLastName: sha256(this.state.last_name),
                            hashedEmail: sha256(this.state.email),
                            hashedNumber: sha256(this.state.phone),
                            pageCategory: 'Business',
                        }
                    });
			})
			.catch(response => {
                globals.handleError(response.message, {info: `Error saving data to ${this.props.submitFormUrl}`, data: this.state});
			});
	}

    getPricing() {
        animations.showLoader(this);
        this.setProgress(true);
        this.resetCarrierState();
        CARRIERS.forEach(carrier => {
            this.fetchPrice(carrier).then(() => {

            });
        });
    }

    async fetchPrice(carrier) {
        const data = {
            address: this.state.address,
            speed: this.state.speed,
            carrier: carrier
        };

        const hasData = result => {
            return result.data.data && result.data.data[mapCarriersToData[carrier]];
        };

        await axios.post(this.props.getPricingUrl, data)
            .then(result => {
                let carrierData = result.data.data[mapCarriersToData[carrier]];
                let carriersDisabled = [...this.state.carriersDisabled];
                let carriersLoaded = [...this.state.carriersLoaded, carrier];
                let plans = {...this.state.plans};

                if (hasData(result)) {
                    plans[carrier] = carrierData;
                } else {
                    carriersDisabled = [...this.state.carriersDisabled, carrier];
                    plans[carrier] = [];
                }

                this.handlePricing(plans, carriersLoaded, carriersDisabled, hasData(result), this.state.carrierErrors);
            })
            .catch(response => {
                globals.handleError(response.message, {info: `Error occurred loading carrier pricing for ${carrier}`, data: data})
                this.handlePricing(
                    {...this.state.plans, [carrier]: []},
                    [...this.state.carriersLoaded, carrier],
                    [...this.state.carriersDisabled],
                    false,
                    [...this.state.carrierErrors, carrier]
                );
            });
    }

    resetCarrierState() {
        this.setState({
            carriersLoaded: [],
            carriersDisabled: [],
            carrierErrors: [],
            countCarriersLoaded: 0,
            countCarriersWithData: 0,
        });
    }

	setProgress(value) {
		this.setState({
			inProgress: value
		}, () => {
			animations.toggleSpeedButtons(value);
		});
	}

	handlePricing(plans, carriersLoaded, carriersDisabled, hasData, carrierErrors) {
        this.setState({
            plans: plans,
            carriersLoaded: carriersLoaded,
            carriersDisabled: carriersDisabled,
            carrierErrors: carrierErrors,
            countCarriersLoaded: this.state.countCarriersLoaded + 1,
            countCarriersWithData: hasData ? this.state.countCarriersWithData + 1 : this.state.countCarriersWithData,
        }, () => {
            // Once we have received one response show the table
            if (this.state.countCarriersLoaded === 1) {
                animations.showSuccess(this);
            }

            if (this.state.countCarriersLoaded === CARRIERS.length) {
                this.setProgress(false);
            }
        });
	}

	handleCloseModal(event) {
    	event.preventDefault();
    	animations.closeError(this, '#no-fibre-error');
    	this.setState({plans: {}});
	}

	handleCloseError(event) {
		event.preventDefault();
		animations.closeError(this, '#internal-error');
	}

    processValidation(response) {
        let PartnersFormNode = findDOMNode(this),
            errors = response.data.errors;

        for (let key in errors) {
            if (key === this.honeypot || key === 'speed') {
                // do nothing
            } else {
                let selector = PartnersFormNode.querySelector('.input-' + key + ' .graphic--nao');
                if (selector) {
                    selector.style.stroke = 'red';
                }
            }

            this.updateErrors(errors[key][0]);
        }

        animations.showErrors(this);
    }

    updateErrors(newError) {
        this.setState(prevState => ({
            errors: [...prevState.errors, newError]
        }));
    }

    handleError(response) {
        this.updateErrors(globals.getGenericErrorMsg());
        animations.showErrors(this);
        globals.handleError(response.message, response);
    }

    reset() {
        this.setState({errors: []});
        animations.reset(this);
    }

    render() {
        return (
            <div className="price-checker-form" id="price-checker-form">
				<div className="inner">
					<div ref={el => this.content = el}>
						<div id="business-fibre-anchor" />
						<Heading title={this.props.title} subTitle={this.props.subTitle}>
							<div className="row">
								<div className="col-md-6 col-md-offset-3 col-xs-12 centered">
									{this.props.leadCopy ? <p>{this.props.leadCopy}</p> : ''}
								</div>
							</div>
						</Heading>
						<div className="row form-outer">
							<div className="col-md-10 col-md-offset-1 col-xs-12">
								<form onSubmit={this.handleSubmit.bind(this)}>
									<InternetForm
										state={this.state}
										honeypot={this.honeypot}
										handleSetAddress={this.handleSetAddress.bind(this)}
										handleInputChange={this.handleInputChange.bind(this)}
									/>
									<div className="animate-up form-row speed-btns">
										<SpeedButtons
											speeds={this.props.speeds}
											handleSpeedChange={this.handleSpeedChange.bind(this)}
											speed={this.state.speed}
										/>
									</div>
									<Errors errors={this.state.errors} />
									<PricingBtn handleSubmit={event => this.handleSubmit(event)}>
										<LoaderText title="Calculating pricing" subTitle="(may take up to 20 seconds)" />
									</PricingBtn>
                                    <PlanResultsTableAsync
                                        plans={this.state.plans}
                                        speed={this.state.speed}
                                        carriersDisabled={this.state.carriersDisabled}
                                        carriersLoaded={this.state.carriersLoaded}
                                        carrierErrors={this.state.carrierErrors}
                                        hasResults={this.state.countCarriersWithData > 0}
                                        completedWithoutResults={this.state.countCarriersLoaded === CARRIERS.length && this.state.countCarriersWithData === 0}
                                    >
                                        <Disclaimer1 />
                                    </PlanResultsTableAsync>
								</form>
								<PricingError id="no-fibre-error" handleClose={this.handleCloseModal.bind(this)}>
									<p>Your address is not showing up as enabled for fibre at this time.</p>
									<p>Please give our team a call on 1300 393 835 as we do have other options.</p>
								</PricingError>
								<InternalPricingError handleCloseError={this.handleCloseError.bind(this)} />
							</div>
						</div>
					</div>
				</div>
				<div className="bottom-wave-darkgreen" />
            </div>
        );
    }

}

PriceCheckerForm.propTypes = {
	title: PropTypes.string,
	subTitle: PropTypes.string,
	leadCopy: PropTypes.string,
	speeds: PropTypes.array.isRequired,
	submitFormUrl: PropTypes.string.isRequired,
	getPricingUrl: PropTypes.string.isRequired,
    eventNameSpeed: PropTypes.string.isRequired,
    eventNameSubmit: PropTypes.string.isRequired
};

export default PriceCheckerForm;


// WEBPACK FOOTER //
// ./resources/assets/js/forms/PriceCheckerForm/PriceCheckerForm.js